import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "@/components/atoms";
import { useKyvEnabled, useOperator } from "@/hooks";
import { veivoApi, VeivoValidationFromVin } from "@/redux/apis/vehicle/veivoApi";
import { useAppDispatch } from "@/redux/hooks";
import { KyvVerificationFlow } from "./KyvVerificationFlow";
import { AddVehicleModal } from "./modals/AddVehicleModal";
import { AddVehicleVinModal } from "./modals/AddVehicleVinModal";

interface AddVehicleEntryProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Chooses the add-vehicle front door.
 *
 * With KYV on, it is plate + state into verification: the VIN is read off the
 * registration document, and the vehicle row is created server-side when
 * verification passes, so nothing partial exists here if the operator walks
 * away mid-flow. With KYV off, it is the plate registry lookup — the AU flow,
 * unchanged.
 *
 * See useKyvEnabled for who gets which. Short version: outside Australia KYV
 * is the only path that can work, so it is not negotiable there; inside
 * Australia the flag decides and defaults to the plate flow.
 */
export const AddVehicleEntry = ({ open, onClose }: AddVehicleEntryProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id: operatorId } = useOperator();
  const { enabled: kyvEnabled, isLoading: loadingKyv } = useKyvEnabled();
  const [validation, setValidation] = useState<VeivoValidationFromVin | null>(null);

  const handleClose = () => {
    setValidation(null);
    onClose();
  };

  // Render neither door until we know which one this operator has. Guessing
  // costs more than a flicker here: the KYV modal would be the wrong question
  // for an operator who is about to be handed the plate lookup.
  if (loadingKyv) return null;

  if (!kyvEnabled) {
    return <AddVehicleModal open={open} onClose={onClose} />;
  }

  return (
    <>
      <AddVehicleVinModal open={open && !validation} onClose={handleClose} onStarted={setValidation} />

      <Modal
        open={Boolean(validation)}
        onClose={handleClose}
        title="Verify Vehicle"
        description="Photograph the registration document, then walk around the vehicle."
        variant="web-large"
      >
        {validation && (
          <KyvVerificationFlow
            validation={validation}
            onVehicleCreated={(vehicleUuid) => {
              // The verification is no longer "in progress": drop the cached
              // list so My Vehicles does not keep showing it after setup.
              dispatch(veivoApi.util.invalidateTags(["VeivoValidation"]));
              setValidation(null);
              onClose();
              // Land in the existing wizard to finish RideMinder's own setup —
              // type, capacity, bags, driver access. KYV proves the vehicle is
              // real; this is where it becomes operable.
              navigate(`/operator/${operatorId}/vehicles/my-vehicles/add/${vehicleUuid}`);
            }}
          />
        )}
      </Modal>
    </>
  );
};
